import {
  Box,
  IconButton,
  InputBase,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { tokens } from '../../global/theme/tokens';
import EmployeesMultiSelect from '../../components/select/EmployeesMultiSelect';
import ProjectsMultiSelect from '../../components/select/ProjectsMultiSelect';

// search, employee and project all go to the capacities request
const EmployeeCapacityFilterBar = ({
  users = [],
  projects = [],
  filter,
  onChange,
  employeeCount,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState(filter.search || '');

  // typing should not re-filter on every keystroke
  useEffect(() => {
    const timeout = setTimeout(() => {
      if ((filter.search || '') !== searchInput)
        onChange({ ...filter, search: searchInput });
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput, filter]);

  return (
    <Box
      className="flex flex-col md:flex-row md:items-center justify-between gap-2 rounded-xl px-3 py-2 backdrop-blur-lg shrink-0"
      sx={{ backgroundColor: colors.glass }}
    >
      <Box className="flex flex-col md:flex-row md:items-center gap-2 grow min-w-0">
        <Box
          className="flex items-center gap-1 rounded-lg px-2 w-full md:max-w-[260px]"
          sx={{
            backgroundColor: colors.bgInput,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${colors.text}20`,
            ':focus-within': { borderColor: `${colors.info}80` },
          }}
        >
          <SearchIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
          <InputBase
            value={searchInput}
            placeholder={t('Search employee...')}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearchInput('');
            }}
            sx={{ fontSize: '13px', width: '100%' }}
          />
          {searchInput && (
            <IconButton size="small" onClick={() => setSearchInput('')}>
              <CloseIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          )}
        </Box>

        {/* both pickers share one row below the search box on a phone */}
        <Box className="flex flex-wrap items-center gap-2">
          <EmployeesMultiSelect
            users={users}
            selectedIds={filter.userIds}
            onChange={(ids) => onChange({ ...filter, userIds: ids })}
          />

          <ProjectsMultiSelect
            projects={projects}
            selectedIds={filter.projectIds}
            onChange={(ids) => onChange({ ...filter, projectIds: ids })}
          />

          {/* finished tasks stay hidden until they are asked for */}
          <Tooltip title={t('Hide finished tasks')}>
            <ToggleButton
              size="small"
              value="hideDone"
              selected={Boolean(filter.hideDone)}
              onChange={() =>
                onChange({ ...filter, hideDone: !filter.hideDone })
              }
              sx={{ padding: '5px', borderRadius: '8px' }}
            >
              <RemoveDoneIcon sx={{ fontSize: '16px' }} />
            </ToggleButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography
        fontSize="12px"
        className="opacity-60 whitespace-nowrap shrink-0"
      >
        {`${employeeCount} ${t('Employees')}`}
      </Typography>
    </Box>
  );
};

export default EmployeeCapacityFilterBar;
